// ---------------------------------------------------------------------------
// useContinueEncounter
//
// React hook that powers the explicit "Continue encounter" buttons on the
// patient detail view (and the session detail view, and anywhere else we
// might want to give a provider a one-tap way to add a new Conversation to
// an existing Encounter).
//
// The hook handles three responsibilities so the call sites stay tiny:
//
//   1. Reject continuation of a recording-in-progress encounter — only
//      closed encounters can be continued. UI surfaces should already be
//      guarding via `isEncounterContinuable`, but the hook double-checks
//      defensively so an in-flight state change can't race a tap.
//
//   2. Materialize an Encounter doc just-in-time for implicit encounters
//      (legacy single-Conversation rows that have no `encounter_id`).
//      Continuing a legacy session writes the real Encounter doc, stamps
//      `encounter_id` back onto the seed Conversation, and then proceeds
//      as if the encounter were real all along.
//
//   3. Navigate to the Scribe surface with the right deep-link state so
//      <ScribePage> skips its implicit pre-check modal and binds the new
//      Conversation to the right encounter on Start.
//
// On failure the hook toasts and stays put — never silently navigates
// without an encounter linkage, which would create an orphan Conversation
// that looks like it was supposed to be combined but isn't.
// ---------------------------------------------------------------------------

import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useToast} from '@chakra-ui/react';
import {useUser} from '../contexts/UserContext';
import {useAuth} from '../contexts/AuthContext';
import {
  isEncounterContinuable,
  isEncounterRecording,
  isEncounterVisibleToUser,
  resolveMintedEncounterIdForSyntheticView,
  setEncounterLanguage,
} from '../services/encounterService';
import {ehrService} from '../services/ehrService';
import ContinueModePickerModal from '../components/common/ContinueModePickerModal';
import {buildLiveEncounterRoute} from '../utils/encounterLaunch';
import {SCRIBE_ROUTE, buildLiveScribeRouteState} from '../utils/scribeLaunch';
import {
  LIVE_ENCOUNTER_WORKFLOW_TYPE,
  TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
  isPhoneEncounter,
  isTelehealthEncounter,
} from '../services/encounterUtils';

// Workflow types the Continue flow knows how to resume natively. Anything
// outside this set (telephonic live encounters, telehealth live encounter
// variants, etc.) is treated as a Scribe-shaped encounter for routing
// purposes — they all create the same Conversation document shape, so
// landing in the Scribe surface still produces a linked session. We
// keep the explicit check so future workflow types can be added here
// rather than scattered across call sites.
const LIVE_ENCOUNTER_MODE = 'encounter';
const LIVE_ENCOUNTER_WORKFLOW = LIVE_ENCOUNTER_WORKFLOW_TYPE;
const LIVE_ENCOUNTER_WORKFLOWS = new Set([
  LIVE_ENCOUNTER_WORKFLOW,
  TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
]);

function isLiveEncounterWorkflow(workflowType: any) {
  return LIVE_ENCOUNTER_WORKFLOWS.has(workflowType || '');
}

function normalizeContinueMode(mode: any) {
  if (mode === LIVE_ENCOUNTER_WORKFLOW) return LIVE_ENCOUNTER_MODE;
  return mode;
}

/**
 * Patient context the navigation expects on `location.state`. ScribePage
 * already reads `patient` from state for the new-session flow, so we just
 * compose one that matches that shape (local fields + `_ehrPatientId` for
 * EHR-resolved ids the picker leaves on the object).
 */
function buildNavPatient(encounter: any) {
  if (!encounter) return null;
  const localId = encounter.patient_id || null;
  const ehrId = encounter.ehr_patient_id || null;
  if (!localId && !ehrId) return null;
  const [first, ...rest] = (encounter.patient_name || '').split(' ');
  return {
    // Never let an EHR id occupy the local-id slot: an EHR-only patient must
    // keep `id` null so downstream local CRUD / session routing can't treat it
    // as a local patient (cross-patient hazard).
    id: localId,
    first_name: first || '',
    last_name: rest.join(' '),
    _isEhrOnly: !localId,
    _ehrPatientId: ehrId,
    ehr_patient_id: ehrId,
    patient_id: localId,
  };
}

/**
 * The interpretation language already recorded for an encounter, resolved from
 * the encounter doc first and then its seed (earliest) conversation. Returns a
 * trimmed `{language, dialect}`; `language` is empty when nothing was ever
 * captured — the signal the Continue flow uses to prompt for one.
 */
function resolveSavedEncounterLanguage(encounter: any) {
  const seed = encounter?.conversations?.[0];
  const language =
    encounter?.foreign_language ||
    seed?.foreign_language ||
    seed?.foreignLanguage ||
    seed?.language ||
    '';
  const dialect = seed?.dialect || '';
  return {
    language: String(language || '').trim(),
    dialect: String(dialect || '').trim(),
  };
}

export function useContinueEncounter() {
  const navigate = useNavigate();
  const toast = useToast();
  const {userData} = useUser();
  const {currentUser} = useAuth();
  const [isContinuing, setIsContinuing] = useState(false);
  // Encounter awaiting a modality choice in the picker modal. Null when the
  // picker is closed. Set by `continueEncounter` once the continuable guards
  // pass; cleared when the provider picks a mode or dismisses the picker.
  const [pendingEncounter, setPendingEncounter] = useState(null);

  // The actual continuation: materialize an implicit encounter if needed,
  // then navigate to the chosen modality's surface with the encounter
  // linkage. `mode` is 'scribe' | 'encounter'. When `mode` is null we
  // fall back to routing by the encounter's own `workflow_type` so any
  // legacy caller that hasn't adopted the picker keeps its old behavior.
  //
  // Assumes the continuable / account-context guards already ran in
  // `continueEncounter` (the single entry point), so we don't re-toast here.
  const runContinue = useCallback(
    async (encounter: any, mode = null, opts: Record<string, any> = {}) => {
      if (!encounter) return;

      setIsContinuing(true);
      try {
        let encounterId = encounter.id;

        // Implicit (legacy) encounter — Schema V2 keeps a REAL minted
        // Encounter doc (`enc-{seedConversationId}`) for every legacy
        // session, so continuing normally just means joining it: the new
        // session binds via `encounter_id` and the server-side projection
        // handles membership, contributors and aggregates. No client-side
        // Encounter create/attach transaction needed. (The pre-V2 client-side
        // materialization fallback is retired with client Firestore writes;
        // when the minted twin can't be verified we continue against the
        // synthetic id, which the destination treats as a standalone start.)
        if (encounter.isSynthetic) {
          const mintedId =
            await resolveMintedEncounterIdForSyntheticView(encounter);
          if (mintedId) {
            encounterId = mintedId;
          } else {
            console.warn(
              '[useContinueEncounter] minted encounter unavailable for synthetic view; starting standalone',
              {encounterId: encounter.id},
            );
            encounterId = null;
          }
        }

        // Compose the deep-link state the destination view expects. We
        // pass the resolved patient (so the picker is pre-filled) and
        // the encounter id (so the destination knows to bind the next
        // Conversation to it). We deliberately don't pass `templateId`
        // here — template selection happens after recording on the
        // iOS/web split we shipped earlier; threading a template hint
        // would resurrect dead UI surface.
        const navPatient = buildNavPatient(encounter);

        // Modality-aware destination. The provider's explicit pick (`mode`)
        // wins: an encounter that started as Scribe can be continued with live
        // translation and vice versa, both modalities link the new
        // Conversation into the same Encounter, so the combined note /
        // transcript merges every contribution.
        //
        // When `mode` is null (legacy caller that hasn't adopted the
        // picker), we fall back to routing by the encounter's own
        // workflow_type. Falling back to Scribe for an unrecognized
        // workflow type is intentional — every workflow that produces a
        // `Conversation` document currently routes through Scribe-shaped
        // persistence, so this default keeps Continue working on
        // rare/legacy values.
        const workflowType = encounter.workflow_type || '';
        const resolvedMode =
          normalizeContinueMode(mode) ||
          (isLiveEncounterWorkflow(workflowType)
            ? LIVE_ENCOUNTER_MODE
            : 'scribe');
        if (resolvedMode === LIVE_ENCOUNTER_MODE) {
          // The encounter pipeline keys off `location.state.language`. The
          // language is either already saved on the encounter, or freshly
          // picked in the mode picker's language step (passed via `opts`).
          const saved = resolveSavedEncounterLanguage(encounter);
          // A freshly picked language (from the modal's language step) wins
          // over whatever the encounter had; we persist it below so future
          // continuations resolve it without prompting again.
          const pickedLanguage = String(opts?.language || '').trim();
          const language = pickedLanguage || saved.language;
          const dialect = pickedLanguage
            ? String(opts?.dialect || '').trim()
            : saved.dialect;
          const baseState = {
            patient: navPatient,
            patientId: navPatient?.id || null,
            // Carry the EHR id separately so an EHR-only continuation keeps its
            // identity (consumed downstream by useEncounterLauncher). `patientId`
            // stays null for EHR-only patients — the EHR id must never occupy
            // the local-id slot.
            ehrPatientId: navPatient?._ehrPatientId || null,
            patientName:
              encounter.patient_name ||
              (navPatient
                ? `${navPatient.first_name || ''} ${navPatient.last_name || ''}`.trim()
                : ''),
            continueEncounterId: encounterId,
            // The modality of the continuation itself, not the encounter's
            // original workflow_type. When a Scribe encounter is continued
            // as a live encounter, this carries the live encounter wire value.
            continueWorkflowType: LIVE_ENCOUNTER_WORKFLOW,
          };
          if (!language) {
            // Defensive: the mode picker collects a language before it ever
            // resolves live-translation for a language-less encounter, so this
            // only trips for a direct `{mode: 'encounter'}` caller (e.g. a
            // test) that couldn't route through the picker's language step.
            toast({
              title: 'Choose a language from Home',
              description:
                'This encounter does not have a saved language to continue live translation.',
              status: 'info',
              duration: 4000,
            });
            return;
          }
          // Persist a freshly picked language onto the encounter (server-side,
          // via the data layer) so it's remembered next time. Best-effort: a
          // write failure must not block starting the session the provider
          // just asked for, so we log and continue with the in-memory choice.
          if (pickedLanguage && encounterId) {
            try {
              await setEncounterLanguage({
                encounterId,
                language,
                dialect,
              });
            } catch (persistErr: any) {
              console.warn(
                '[useContinueEncounter] failed to persist encounter language; continuing with in-memory choice',
                {encounterId, error: persistErr?.message},
              );
            }
          }
          const encounterRoute = buildLiveEncounterRoute({
            ...baseState,
            language,
            dialect,
          });
          navigate(encounterRoute.path, {
            state: encounterRoute.state,
            replace: false,
          });
        } else {
          navigate(SCRIBE_ROUTE, {
            state: buildLiveScribeRouteState({
              patient: navPatient,
              continueEncounterId: encounterId,
            }),
            // Replace history so a back-button press doesn't re-trigger
            // the continue side-effect. The Scribe page itself manages
            // its own forward history once recording begins.
            replace: false,
          });
        }

        // Best-effort EHR encounter sync so the post-session EHR push step
        // sees today's appointments. Same call shape as the regular
        // new-session flow — keeps cross-flow behavior consistent.
        const ehrId =
          encounter.ehr_patient_id ||
          ehrService.resolveEhrPatientId(navPatient) ||
          null;
        if (ehrId && userData?.companyId) {
          ehrService
            .fetchEhrConfig(userData.companyId)
            .then((cfg: any) => {
              if (cfg && cfg.active) {
                ehrService.syncPatientEncounters(ehrId).catch(() => {
                  /* non-fatal */
                });
              }
            })
            .catch(() => {
              /* non-fatal */
            });
        }
      } catch (err) {
        console.error('[useContinueEncounter] continue failed', err);
        toast({
          title: 'Couldn\u2019t continue this encounter',
          description:
            err?.message || 'Try again, or refresh the patient view.',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setIsContinuing(false);
      }
    },
    [navigate, toast, userData, currentUser],
  );

  // Entry point for the "Continue encounter" buttons. Runs the continuable
  // guards once, then either continues immediately (when the caller passes
  // an explicit `mode`) or opens the modality picker so the provider can
  // choose Scribe vs live translation.
  const continueEncounter = useCallback(
    async (encounter: any, opts: Record<string, any> = {}) => {
      if (!encounter) return;

      // Defense-in-depth: the UI shouldn't even render the Continue button
      // for a recording-in-progress encounter, but a race between a stale
      // patient-detail render and a fresh "Recording in progress" status
      // could still slip a tap through.
      if (isEncounterRecording(encounter)) {
        toast({
          title: 'Encounter is still recording',
          description:
            'Wait until the current session ends before adding to this encounter.',
          status: 'info',
          duration: 4000,
        });
        return;
      }
      if (!isEncounterContinuable(encounter)) {
        toast({
          title: 'This encounter can\u2019t be continued',
          description:
            'It may have been removed or marked done. Refresh the page and try again.',
          status: 'warning',
          duration: 4000,
        });
        return;
      }
      // Session privacy: a private encounter is only continuable by its
      // contributors (list surfaces already hide it from everyone else;
      // this guards a stale render or a deep link).
      if (
        !isEncounterVisibleToUser(encounter, {
          userId: currentUser?.uid,
          role: userData?.role,
        })
      ) {
        toast({
          title: 'This session is private',
          description:
            'It belongs to another provider and can\u2019t be viewed or continued.',
          status: 'info',
          duration: 4000,
        });
        return;
      }
      // Only in-person encounters can be continued. Telephone and video
      // (telehealth) encounters run on separate pipelines the Continue flow
      // can't resume, so we block them here defensively — the call sites also
      // hide the Continue affordance for these modalities.
      if (isPhoneEncounter(encounter) || isTelehealthEncounter(encounter)) {
        toast({
          title: 'Only in-person encounters can be continued',
          description:
            'Start a new phone or video encounter from Home instead.',
          status: 'info',
          duration: 4000,
        });
        return;
      }
      if (!userData?.companyId || !currentUser?.uid) {
        toast({
          title: 'Missing account context',
          description: 'Sign out and back in, then try again.',
          status: 'error',
          duration: 4000,
        });
        return;
      }

      // Explicit modality (e.g. a future caller, or a test) skips the picker.
      if (opts && opts.mode) {
        await runContinue(encounter, opts.mode);
        return;
      }

      // Otherwise open the picker; the modality choice resumes via
      // `handlePickerSelect` below.
      setPendingEncounter(encounter);
    },
    [runContinue, toast, userData, currentUser],
  );

  const handlePickerSelect = useCallback(
    (mode: any, extra: Record<string, any> = {}) => {
      const enc = pendingEncounter;
      setPendingEncounter(null);
      if (enc) runContinue(enc, mode, extra);
    },
    [pendingEncounter, runContinue],
  );

  // Whether picking "Interpreter on" for the pending encounter needs to prompt
  // for a language (none was ever captured). Drives the picker's second step
  // instead of the old dead-end toast.
  const pendingSavedLanguage = pendingEncounter
    ? resolveSavedEncounterLanguage(pendingEncounter)
    : {language: '', dialect: ''};

  // Render-ready picker element. Call sites render `{continueModePicker}`
  // once in their tree; it's a no-op (closed modal) until `continueEncounter`
  // sets a pending encounter.
  const continueModePicker = (
    <ContinueModePickerModal
      isOpen={Boolean(pendingEncounter)}
      encounter={pendingEncounter}
      scribeDisabled={userData?.scribeDisabled === true}
      liveNeedsLanguage={!pendingSavedLanguage.language}
      defaultLanguage={pendingSavedLanguage.language}
      defaultDialect={pendingSavedLanguage.dialect}
      onSelect={handlePickerSelect}
      onClose={() => setPendingEncounter(null)}
    />
  );

  return {continueEncounter, isContinuing, continueModePicker};
}
